import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { ChevronLeft, MapPin, Truck, CreditCard, Loader2, Calendar, AlertCircle, CheckCircle, Sparkles, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [outfit, setOutfit] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deliveryOptions, setDeliveryOptions] = useState([]);
  const [postcodeChecking, setPostcodeChecking] = useState(false);
  const [postcodeStatus, setPostcodeStatus] = useState(null); // { valid: bool, message: string }
  const [formData, setFormData] = useState({ 
    customerName: "", 
    customerEmail: "", 
    customerPhone: "", 
    deliveryMethod: "pickup", 
    deliveryAddress: "",
    postcode: "",
    suburb: "" 
  });
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("bookingData");
    if (!stored) { toast.error("Please select your event date first"); navigate(`/outfit/${id}`); return; }
    const data = JSON.parse(stored);
    if (data.outfitId !== id) { navigate(`/outfit/${id}`); return; }
    setBookingData(data);
    fetchOutfit();
    fetchDeliveryOptions();
  }, [id, navigate]);

  const fetchOutfit = async () => {
    try { const response = await axios.get(`${API}/outfits/${id}`); setOutfit(response.data); } 
    catch (error) { toast.error("Error loading outfit"); } 
    finally { setLoading(false); }
  };

  const fetchDeliveryOptions = async () => {
    try {
      const response = await axios.get(`${API}/delivery-options`);
      setDeliveryOptions(response.data.options);
      // Set default to first available option
      if (response.data.options.length > 0) {
        setFormData(p => ({ ...p, deliveryMethod: response.data.options[0].id }));
      }
    } catch (error) {
      console.error("Error fetching delivery options:", error);
      // Fallback to default options
      setDeliveryOptions([
        { id: "pickup", label: "Local Pickup (Free)", description: "By appointment. Pickup location shared after booking.", fee: 0 },
        { id: "delivery", label: "Local Delivery — $15", description: "Delivered 1–2 days before event. Within 15km of Oakleigh South only.", fee: 15 }
      ]);
    }
  };

  const checkPostcode = async (postcode) => {
    if (!postcode || postcode.length < 4) {
      setPostcodeStatus(null);
      return;
    }
    
    try {
      setPostcodeChecking(true);
      const response = await axios.get(`${API}/service-area/check?postcode=${postcode}`);
      setPostcodeStatus({
        valid: response.data.is_serviced,
        message: response.data.is_serviced 
          ? "Great! We deliver to your area." 
          : response.data.message || "Sorry, we don't deliver to this area yet."
      });
    } catch (error) {
      setPostcodeStatus({ valid: false, message: "Unable to verify postcode." });
    } finally {
      setPostcodeChecking(false);
    }
  };

  const handlePostcodeChange = (e) => {
    const postcode = e.target.value.replace(/\D/g, '').slice(0, 4);
    setFormData(p => ({ ...p, postcode }));
    if (postcode.length === 4) {
      checkPostcode(postcode);
    } else {
      setPostcodeStatus(null);
    }
  };

  const getDeliveryFee = () => {
    const option = deliveryOptions.find(o => o.id === formData.deliveryMethod);
    return option?.fee || 0;
  };
  
  const hasBundle = bookingData?.bundleItems?.length > 0;
  const bundleDiscount = bookingData?.bundleDiscount || 0;
  const baseTotal = bookingData ? bookingData.total : 0;
  
  const getTotalWithDelivery = () => {
    if (!bookingData) return 0;
    if (hasBundle) {
      return bookingData.discountedTotal + getDeliveryFee();
    }
    return baseTotal + getDeliveryFee();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerName || !formData.customerEmail || !formData.customerPhone) { 
      toast.error("Please fill all required fields"); 
      return; 
    }
    if (!termsAccepted) {
      toast.error("Please agree to the Borrowing Terms before proceeding");
      return;
    }
    if (formData.deliveryMethod === "delivery") {
      if (!formData.deliveryAddress) { 
        toast.error("Please enter delivery address"); 
        return; 
      }
      if (!formData.postcode || formData.postcode.length !== 4) {
        toast.error("Please enter a valid postcode");
        return;
      }
      if (postcodeStatus && !postcodeStatus.valid) {
        toast.error("Sorry, we don't deliver to this area yet. Please choose pickup instead.");
        return;
      }
    }
    try {
      setSubmitting(true);
      const bookingResponse = await axios.post(`${API}/bookings`, {
        outfit_id: id, 
        customer_name: formData.customerName, 
        customer_email: formData.customerEmail, 
        customer_phone: formData.customerPhone,
        event_date: bookingData.eventDate,
        rental_days: bookingData.rentalDays || 1,
        delivery_method: formData.deliveryMethod,
        delivery_address: formData.deliveryMethod === "delivery" ? formData.deliveryAddress : null,
        postcode: formData.deliveryMethod === "delivery" ? formData.postcode : null,
        suburb: formData.deliveryMethod === "delivery" ? formData.suburb : null,
        terms_accepted_at: new Date().toISOString(),
        terms_accepted_ip: "client"
      });
      const paymentResponse = await axios.post(`${API}/payments/create-session`, { 
        booking_id: bookingResponse.data.id, 
        origin_url: window.location.origin 
      });
      window.location.href = paymentResponse.data.checkout_url;
    } catch (error) { 
      toast.error(error.response?.data?.detail || "Error creating booking"); 
      setSubmitting(false); 
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#9A7B4F]" /></div>;
  if (!outfit || !bookingData) return null;

  const dateInfo = bookingData.dateInfo;

  return (
    <div className="min-h-screen bg-[#FAF8F0] relative">
      <div className="petal-left hidden lg:block" />
      <div className="petal-right hidden lg:block" />
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[11px] text-[#676664]/40 hover:text-[#9A7B4F] mb-8">
          <ChevronLeft className="w-3 h-3" /> Back
        </button>

        <div className="text-center mb-10 animate-fadeIn">
          <p className="text-[#9A7B4F] text-[11px] tracking-[0.3em] uppercase mb-3">Checkout</p>
          <h1 className="text-3xl lg:text-4xl text-[#676664]">Complete Your Booking</h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6 animate-fadeIn stagger-1">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white p-6 border border-[#282828]/10">
                <h2 className="text-sm text-[#676664] mb-6 tracking-wide">Contact Details</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[#676664]/60 text-[11px]">Full Name *</Label>
                    <Input 
                      name="customerName" 
                      value={formData.customerName} 
                      onChange={(e) => setFormData(p => ({...p, customerName: e.target.value}))} 
                      className="mt-2 border-[#282828]/10" 
                      data-testid="customer-name-input" 
                      required 
                    />
                  </div>
                  <div>
                    <Label className="text-[#5A5A5A] text-[12px] font-medium">Email *</Label>
                    <Input 
                      name="customerEmail" 
                      type="email" 
                      value={formData.customerEmail} 
                      onChange={(e) => setFormData(p => ({...p, customerEmail: e.target.value}))} 
                      className="mt-2 border-[#282828]/12 text-[14px]" 
                      data-testid="customer-email-input" 
                      required 
                    />
                  </div>
                  <div>
                    <Label className="text-[#5A5A5A] text-[12px] font-medium">Phone *</Label>
                    <Input 
                      name="customerPhone" 
                      type="tel" 
                      value={formData.customerPhone} 
                      onChange={(e) => setFormData(p => ({...p, customerPhone: e.target.value}))} 
                      className="mt-2 border-[#282828]/12 text-[14px]" 
                      data-testid="customer-phone-input" 
                      required 
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 border border-[#282828]/10">
                <h2 className="text-[15px] text-[#1A1A1A] mb-2 font-medium tracking-wide">Delivery Method</h2>
                <p className="text-[12px] text-[#7A7A7A] mb-6">Melbourne only — within 15km of Oakleigh South</p>
                
                <RadioGroup 
                  value={formData.deliveryMethod} 
                  onValueChange={(v) => {
                    setFormData(p => ({...p, deliveryMethod: v}));
                    if (v === "pickup") {
                      setPostcodeStatus(null);
                    }
                  }} 
                  className="space-y-3"
                >
                  {deliveryOptions.map((option) => {
                    const IconComponent = option.id === "pickup" ? MapPin : Truck;
                    return (
                      <div 
                        key={option.id}
                        className={`flex items-start gap-4 p-4 border cursor-pointer transition-colors ${
                          formData.deliveryMethod === option.id 
                            ? "border-[#9A7B4F] bg-[#D4B8B4]/10" 
                            : "border-[#282828]/10 hover:border-[#282828]/20"
                        }`}
                      >
                        <RadioGroupItem value={option.id} id={option.id} data-testid={`${option.id}-option`} />
                        <Label htmlFor={option.id} className="flex-1 cursor-pointer">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <IconComponent className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                              <span className="text-[14px] text-[#1A1A1A] font-medium">{option.label}</span>
                            </div>
                            <span className={`text-[12px] font-semibold ${option.fee === 0 ? "text-[#9A7B4F]" : "text-[#5A5A5A]"}`}>
                              {option.fee === 0 ? "FREE" : `$${option.fee.toFixed(2)}`}
                            </span>
                          </div>
                          <p className="text-[12px] text-[#7A7A7A]">{option.description}</p>
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
                
                {formData.deliveryMethod === "delivery" && (
                  <div className="mt-6 space-y-4 animate-fadeIn">
                    {/* Postcode validation */}
                    <div>
                      <Label className="text-[#676664]/60 text-[11px]">Postcode *</Label>
                      <div className="relative mt-2">
                        <Input 
                          name="postcode" 
                          value={formData.postcode} 
                          onChange={handlePostcodeChange}
                          className={`border-[#282828]/10 pr-10 ${
                            postcodeStatus 
                              ? postcodeStatus.valid 
                                ? "border-green-500 focus:border-green-500" 
                                : "border-red-400 focus:border-red-400"
                              : ""
                          }`}
                          placeholder="e.g., 3167"
                          maxLength={4}
                          data-testid="postcode-input" 
                          required 
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {postcodeChecking && <Loader2 className="w-4 h-4 animate-spin text-[#9A7B4F]" />}
                          {!postcodeChecking && postcodeStatus?.valid && <CheckCircle className="w-4 h-4 text-green-500" />}
                          {!postcodeChecking && postcodeStatus && !postcodeStatus.valid && <AlertCircle className="w-4 h-4 text-red-400" />}
                        </div>
                      </div>
                      {postcodeStatus && (
                        <p className={`text-[11px] mt-1 ${postcodeStatus.valid ? "text-green-600" : "text-red-500"}`}>
                          {postcodeStatus.message}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="text-[#676664]/60 text-[11px]">Suburb</Label>
                      <Input 
                        name="suburb" 
                        value={formData.suburb} 
                        onChange={(e) => setFormData(p => ({...p, suburb: e.target.value}))} 
                        className="mt-2 border-[#282828]/10" 
                        placeholder="e.g., Oakleigh"
                        data-testid="suburb-input" 
                      />
                    </div>
                    
                    <div>
                      <Label className="text-[#676664]/60 text-[11px]">Delivery Address *</Label>
                      <Textarea 
                        name="deliveryAddress" 
                        value={formData.deliveryAddress} 
                        onChange={(e) => setFormData(p => ({...p, deliveryAddress: e.target.value}))} 
                        className="mt-2 border-[#282828]/10" 
                        placeholder="Street address, unit number, etc."
                        data-testid="delivery-address-input" 
                        required 
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-[#F0D9D5]/20 p-5">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-4 h-4 text-[#9A7B4F] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <h4 className="text-[11px] text-[#676664] mb-1">Secure Payment</h4>
                    <p className="text-[10px] text-[#676664]/60 font-light">
                      Redirected to secure payment. Security bond* fully refundable upon return.
                    </p>
                  </div>
                </div>
              </div>

              {/* Terms Acceptance */}
              <div className="border border-[#282828]/10 p-5">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="terms-checkbox-label">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-[#282828]/20 text-[#9A7B4F] focus:ring-[#9A7B4F] accent-[#9A7B4F]"
                    data-testid="terms-checkbox"
                  />
                  <span className="text-[13px] text-[#676664]">
                    I agree to the{" "}
                    <Link to="/borrowing-terms" target="_blank" className="text-[#9A7B4F] underline underline-offset-2 hover:text-[#8B6B3F]">
                      Borrowing Terms
                    </Link>
                  </span>
                </label>
                <p className="text-[11px] text-[#676664]/50 font-light mt-2 ml-7">
                  Bookings become non-refundable within 3 days of your event date. Date changes void cancellation eligibility.
                </p>
              </div>

              <Button 
                type="submit" 
                disabled={submitting || !termsAccepted || (formData.deliveryMethod === "delivery" && postcodeStatus && !postcodeStatus.valid)} 
                className="w-full bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white py-6 text-[11px] tracking-[0.15em] uppercase disabled:opacity-50" 
                data-testid="proceed-to-payment-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" />Processing...</>
                ) : (
                  <>Proceed to Payment — ${getTotalWithDelivery().toFixed(2)}</>
                )}
              </Button>
            </form>
          </div>

          <div className="animate-fadeIn stagger-2">
            <div className="bg-white p-6 border border-[#282828]/10 sticky top-28">
              <h2 className="text-[15px] text-[#1A1A1A] mb-6 font-medium tracking-wide">Order Summary</h2>
              <div className="flex gap-4 mb-6 pb-6 border-b border-[#282828]/10">
                <div className="w-20 h-28 overflow-hidden bg-[#FAF8F0]">
                  <img 
                    src={outfit.images[0] || "https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400"} 
                    alt={outfit.name} 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div>
                  <p className="text-[11px] text-[#9A7B4F] tracking-[0.12em] uppercase mb-1 font-medium">{outfit.category}</p>
                  <h3 className="text-[14px] text-[#1A1A1A] font-medium mb-1">{outfit.name}</h3>
                  <p className="text-[12px] text-[#7A7A7A]">Size: {outfit.size}</p>
                </div>
              </div>
              
              {/* Rental Schedule */}
              {dateInfo && (
                <div className="space-y-3 mb-6 pb-6 border-b border-[#282828]/10">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-4 h-4 text-[#9A7B4F]" strokeWidth={1.5} />
                    <span className="text-[12px] text-[#1A1A1A] font-medium">Rental Schedule</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-[12px]">
                    <div>
                      <p className="text-[#7A7A7A]">Event Date</p>
                      <p className="text-[#9A7B4F] font-semibold">{format(new Date(dateInfo.event_date + "T00:00:00"), "EEE, MMM d")}</p>
                    </div>
                    <div>
                      <p className="text-[#7A7A7A]">Duration</p>
                      <p className="text-[#1A1A1A] font-medium">{bookingData.rentalDays || 1} {(bookingData.rentalDays || 1) === 1 ? 'day' : 'days'}</p>
                    </div>
                    <div>
                      <p className="text-[#7A7A7A]">Dispatch</p>
                      <p className="text-[#1A1A1A] font-medium">{format(new Date(dateInfo.delivery_date + "T00:00:00"), "EEE, MMM d")}</p>
                    </div>
                    <div>
                      <p className="text-[#7A7A7A]">Return By</p>
                      <p className="text-[#1A1A1A] font-medium">{format(new Date(dateInfo.return_by_date + "T00:00:00"), "EEE, MMM d")}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-2.5 text-[12px]">
                {/* Selected Rental Option */}
                {bookingData.rentalOptionId && (
                  <div className="flex justify-between pb-2 border-b border-[#282828]/5">
                    <span className="text-[#7A7A7A]">Set Option</span>
                    <span className="text-[#1A1A1A] font-medium">{outfit.rental_options?.find(o => o.id === bookingData.rentalOptionId)?.name || "Standard"}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[#7A7A7A]">Rental Duration</span>
                  <span className="text-[#1A1A1A]">{bookingData.rentalDays || 1} Days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#676664]/50 font-light">Rental Fee</span>
                  <span>${bookingData.rentalCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#676664]/50 font-light">Security Bond*</span>
                  <span>${bookingData.deposit.toFixed(2)}</span>
                </div>

                {/* Bundle Items */}
                {hasBundle && (
                  <>
                    <div className="border-t border-[#282828]/10 pt-2 mt-2">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Sparkles className="w-3 h-3 text-[#9A7B4F]" strokeWidth={1.5} />
                        <span className="text-[10px] text-[#9A7B4F] tracking-[0.1em] uppercase font-medium">Bundle Items</span>
                      </div>
                      {bookingData.bundleItems.map((bi) => (
                        <div key={bi.id} className="flex justify-between py-0.5" data-testid={`checkout-bundle-${bi.id}`}>
                          <span className="text-[#676664]/50 font-light truncate pr-2">{bi.name}</span>
                          <span className="flex-shrink-0">${(bi.price_4_day || bi.price_per_day || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-[#282828]/10 pt-2 mt-2 space-y-1">
                      <div className="flex justify-between" data-testid="checkout-original-total">
                        <span className="text-[#676664]/50 font-light">Original Total</span>
                        <span className="line-through text-[#676664]/40">${bookingData.originalTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-[#9A7B4F]" data-testid="checkout-savings">
                        <span className="font-medium">Bundle Savings ({bundleDiscount}%)</span>
                        <span className="font-medium">-${bookingData.discountAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex justify-between">
                  <span className="text-[#676664]/50 font-light">
                    {formData.deliveryMethod === "pickup" ? "Local Pickup" : "Local Delivery"}
                  </span>
                  <span className={getDeliveryFee() === 0 ? "text-[#9A7B4F]" : ""}>
                    {getDeliveryFee() === 0 ? "FREE" : `$${getDeliveryFee().toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between pt-3 mt-2 border-t border-[#282828]/10 text-sm">
                  <span className="text-[#676664]">Total</span>
                  <span className="text-[#9A7B4F] font-medium" data-testid="checkout-final-total">${getTotalWithDelivery().toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
